import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAddWorkflowStepComment, useBeginProcessingWorkflowStep, useGetSPLotWorkflowDetails } from '../api/vegApl/vegApl';
import { useMarkAsDone, useSkipStep } from '../api/vegApl/Technician';
import { useCommonStyles } from '../styles/GenericStyle';
import { useGlobalState } from '../contexts/GlobalState';
import { useSnackbar } from '../contexts/SnackbarContext';
import { PATH_NAMES } from '../routes';
import type { ProcessingWorkflowProps } from '../pages/processingWorkflow/types';
import type { ProcessingStep, SPLotInfo, SPLotWorkflowDetailsResponseItem, WorkflowStepAPI, HandheldSuccessSnackbar } from '../pages/processingWorkflow/types';

const getIconForStepName = (stepName: string) => {
    const nameLower = stepName.toLowerCase();
    const iconStyle = { width: 24, height: 24, display: 'block' } as any;
    if (nameLower.includes('blower')) return <img src="icons/blowercleaning_icon.png" alt="Blower Cleaning" style={iconStyle} />;
    if (nameLower.includes('ferment')) return <img src="icons/fermentation_icon.png" alt="Fermentation" style={iconStyle} />;
    if (nameLower.includes('seed') && nameLower.includes('extract')) return <img src="icons/seedextraction_icon.png" alt="Seed Extraction" style={iconStyle} />;
    if (nameLower.includes('extract')) return <img src="icons/seedextraction_icon.png" alt="Extraction" style={iconStyle} />;
    if (nameLower.includes('clean')) return <img src="icons/cleaning_icon.png" alt="Cleaning" style={iconStyle} />;
    if (nameLower.includes('dry') || nameLower.includes('drying')) return <img src="icons/drying_icon.png" alt="Drying" style={iconStyle} />;
    if (nameLower.includes('disinfect')) return <img src="icons/disinfection_icon.png" alt="Disinfection" style={iconStyle} />;
    if (nameLower.includes('heat') || nameLower.includes('treatment')) return <img src="icons/heattreatment_icon.png" alt="Heat Treatment" style={iconStyle} />;
    if (nameLower.includes('centrifug')) return <img src="icons/centrifuging_icon.png" alt="Centrifuging" style={iconStyle} />;
    if (nameLower.includes('pack')) return <img src="icons/packaging_icon.png" alt="Packaging" style={iconStyle} />;
    if (nameLower.includes('mint') || nameLower.includes('reg')) return <img src="icons/mint_icon.png" alt="Mint Registration" style={iconStyle} />;
    return null;
};

const mapApiStatus = (apiStatus: string) => {
    const statusLower = (apiStatus || '').toLowerCase();
    if (statusLower === 'not started') return 'Not Started';
    if (statusLower === 'in progress') return 'In Progress';
    if (statusLower === 'completed') return 'Completed';
    if (statusLower === 'skipped') return 'Skipped';
    return 'Not Started';
};

export const useProcessingWorkflowController = ({ spLotIds: propSpLotIds, onBack, originComponent }: ProcessingWorkflowProps = {}) => {
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as { spLotIds?: string[]; originComponent?: 'scan' | 'processing-card' | 'idle-card' } | null;
    const currentOrigin = originComponent || locationState?.originComponent;
    const spLotIds = locationState?.spLotIds || propSpLotIds || ["MAAG-TOM-20260213-0001"];
    const isBulkView = spLotIds.length > 1;
    const [spLotInfo, setSpLotInfo] = useState<SPLotInfo | null>(null);
    const [processingSteps, setProcessingSteps] = useState<ProcessingStep[]>([]);
    const [expandedStep, setExpandedStep] = useState<number | null>(null);
    const [commentDialogOpen, setCommentDialogOpen] = useState(false);
    const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
    const [spLotsDialogOpen, setSpLotsDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [skipDialogOpen, setSkipDialogOpen] = useState(false);
    const [skipComment, setSkipComment] = useState('');
    const [commentText, setCommentText] = useState('');
    const [originalCommentText, setOriginalCommentText] = useState('');
    const [currentStepForComment, setCurrentStepForComment] = useState<number | null>(null);
    const [expandedInfo, setExpandedInfo] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showScanResourceId, setShowScanResourceId] = useState(false);
    const [scanMode, setScanMode] = useState<'equipment' | 'treatment'>('equipment');
    const [validatedEquipmentId, setValidatedEquipmentId] = useState('');
    const [isActionInProgress, setIsActionInProgress] = useState(false);
    const { crops, state: globalState, setHomePage, isMobile } = useGlobalState();
    const getCropNameByCode = (code: string) => {
        const crop = crops.find((c: any) => c.crop_xrd_oid === code);
        return crop ? crop.name : code;
    };
    const spLotIdsString = useMemo(() => JSON.stringify(spLotIds), [spLotIds]);
    const stepRefs = useRef<Record<number, HTMLDivElement | null>>({});

    useEffect(() => {
        if (!processingSteps || processingSteps.length === 0) return;
        const apiCurrentStep = spLotInfo?.currentStepId
            ? processingSteps.find(step => step.id === spLotInfo.currentStepId)
            : undefined;
        if (apiCurrentStep && expandedStep !== apiCurrentStep.id) {
            setExpandedStep(apiCurrentStep.id);
        }
    }, [processingSteps, spLotInfo?.currentStepId]);

    useEffect(() => {
        if (expandedStep && stepRefs.current[expandedStep]) {
            stepRefs.current[expandedStep]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [expandedStep]);

    const getSPLotWorkflowDetailsMutation = useGetSPLotWorkflowDetails();

    const applyWorkflowDetails = (apiDataArray: SPLotWorkflowDetailsResponseItem[]) => {
        const apiData = apiDataArray[0];
        const spLotData: SPLotInfo = {
            cropCode: apiData.Crop_Code,
            cropName: apiData.Crop_Name || apiData.Crop_Code,
            siteCode: apiData.Site_Code,
            materials: apiData.Materials,
            currentStepId: apiData.current_splot_step_id,
            currentStepStatus: apiData.current_splot_step_status,
            workflowProtocolId: apiData.Workflow_Protocol_id,
            totalSteps: apiData.Workflow_Steps.length,
            spLotNumber: apiData.SP_Lot_Number || apiData.sp_lot_number || apiData.SPLotNumber || apiData.splot_number || ''
        };
        const stepsWithIcons: ProcessingStep[] = apiData.Workflow_Steps.map((step: WorkflowStepAPI) => {
            const stepNameLower = step.workflow_step_name.toLowerCase();
            const mappedStatus = step.workflow_step_id === spLotData.currentStepId
                ? mapApiStatus(spLotData.currentStepStatus)
                : mapApiStatus(step.workflow_step_status);

            return {
                id: step.workflow_step_id,
                name: step.workflow_step_name,
                status: mappedStatus,
                instruction: step.workflow_step_instruction,
                comment: step.workflow_step_comment,
                icon: getIconForStepName(step.workflow_step_name) as any,
                resourceId: step.workflow_step_equipment_id,
                treatmentName: step.splot_step_treatment_name,
                treatmentRequired: Boolean(step.treatment_required),
                mappedTreatments: Array.isArray(step.mapped_treatments) ? step.mapped_treatments : [],
                equipments: Array.isArray(step.equipments) ? step.equipments : [],
                isMandatory: Boolean(step.is_mandatory),
                canProcessOnMobile: step.canProcessOnMobile,
                resourceIdRequired: Boolean(
                    step.resource_id_required ??
                    step.treatment_required ??
                    step.is_resource_id_required
                ),
                disabled: stepNameLower.includes('packaging') || stepNameLower.includes('mint')
            } as ProcessingStep;
        });
        setSpLotInfo(spLotData);
        setProcessingSteps(stepsWithIcons);

        const apiCurrentStep = spLotData.currentStepId
            ? stepsWithIcons.find(step => step.id === spLotData.currentStepId)
            : undefined;

        if (apiCurrentStep) {
            setExpandedStep(apiCurrentStep.id);
        }
    };

    const refreshWorkflowDetails = () => {
        return new Promise<void>((resolve, reject) => {
            getSPLotWorkflowDetailsMutation.mutate(
                { data: { splot_ids: spLotIds } },
                {
                    onSuccess: (response) => {
                        applyWorkflowDetails(response.data as SPLotWorkflowDetailsResponseItem[]);
                        resolve();
                    },
                    onError: (error) => {
                        reject(error);
                    }
                }
            );
        });
    };

    useEffect(() => {
        setLoading(true);
        getSPLotWorkflowDetailsMutation.mutate(
            { data: { splot_ids: spLotIds } },
            {
                onSuccess: (response) => {
                    applyWorkflowDetails(response.data as SPLotWorkflowDetailsResponseItem[]);
                },
                onError: (error) => {
                    showSnackbar({ message: error?.response?.data?.detail || 'Error fetching SP Lot data', severity: 'error' });
                    if (onBack) onBack();
                    console.error('Error fetching SP Lot data:', error);
                },
                onSettled: () => {
                    setLoading(false);
                }
            }
        );
    }, [spLotIdsString]);

    const addCommentMutation = useAddWorkflowStepComment();
    const beginProcessingMutation = useBeginProcessingWorkflowStep();
    const markAsDoneMutation = useMarkAsDone();
    const skipStepMutation = useSkipStep();

    const getCurrentStepFromApi = () => {
        if (!spLotInfo?.currentStepId) return undefined;
        return processingSteps.find(step => step.id === spLotInfo.currentStepId);
    };

    const isCurrentStatus = (status: 'Not Started' | 'In Progress') => {
        return (spLotInfo?.currentStepStatus || '').toLowerCase() === status.toLowerCase();
    };

    const handleAddComment = (stepId: number, existingComment?: string) => {
        setCurrentStepForComment(stepId);
        const commentToEdit = existingComment || '';
        setCommentText(commentToEdit);
        setOriginalCommentText(commentToEdit);
        setCommentDialogOpen(true);
    };

    const handleSaveComment = async () => {
        if (!currentStepForComment || !commentText.trim() || !spLotInfo) return;
        try {
            await addCommentMutation.mutateAsync({
                data: {
                    splot_ids: spLotIds,
                    workflow_step_id: currentStepForComment,
                    workflow_protocol_id: spLotInfo.workflowProtocolId,
                    comment: commentText,
                    user_id: globalState?.user_id_db
                }
            });
            setProcessingSteps(prev =>
                prev.map(step =>
                    step.id === currentStepForComment
                        ? { ...step, comment: commentText }
                        : step
                )
            );
            setCommentText('');
            setCommentDialogOpen(false);
            setCurrentStepForComment(null);
        } catch (error) {
            console.error('Failed to save comment:', error);
        }
    };

    const normalizeStepName = (name: string) => String(name ?? '').trim().toLowerCase();
    const isPackagingStep = (name: string) => {
        const normalized = normalizeStepName(name);
        return normalized.includes('packaging') && (normalized.includes('labelling') || normalized.includes('label'));
    };
    const isMintRegistrationStep = (name: string) => {
        const normalized = normalizeStepName(name);
        return normalized.includes('mint') && normalized.includes('registration');
    };
    const isRestrictedStepForActionButtons = (step: ProcessingStep | undefined) => {
        if (!step) return false;
        return isPackagingStep(step.name) || isMintRegistrationStep(step.name);
    };
    // Matches the web Stepper: only Packaging & Labelling and Mint Registration are blocked.
    const canBeginStep = (step: ProcessingStep | undefined) => {
        if (!step || step.disabled) return false;
        return true;
    };
    const getCurrentStepEquipments = (step?: ProcessingStep) => Array.isArray(step?.equipments) ? step.equipments : [];
    const isCurrentStepTreatmentRequired = (step?: ProcessingStep) => Boolean(step?.treatmentRequired);

    const beginProcessingForStep = async (payload?: { equipment_id?: string; treatment_name?: string }) => {
        if (isActionInProgress) return;
        setIsActionInProgress(true);
        if (isMobile && !isBulkView) setLoading(true);
        try {
            const currentStep = getCurrentStepFromApi();
            const stepToBegin = (currentStep && isCurrentStatus('Not Started')) ? currentStep : undefined;
            if (!stepToBegin || !spLotInfo || !canBeginStep(stepToBegin)) return;
            const requestData: any = {
                comment: '',
                splot_ids: spLotIds,
                user_id: globalState.user_id_db,
                workflow_step_id: stepToBegin.id,
                workflow_protocol_id: spLotInfo.workflowProtocolId,
                equipment_id: ""
            };
            if (payload?.equipment_id) requestData.equipment_id = payload.equipment_id;
            if (payload?.treatment_name) requestData.treatment_name = payload.treatment_name;
            await beginProcessingMutation.mutateAsync({ data: requestData });
            await refreshWorkflowDetails();
            setShowScanResourceId(false);
            setValidatedEquipmentId('');
            const beginSuccessMessage = `${stepToBegin.name} for ${spLotIds[0]} is now in progress`;
            const successSnackbar: HandheldSuccessSnackbar = { message: beginSuccessMessage, severity: 'success', duration: 4500 };
            if (isMobile && !isBulkView) {
                setHomePage(true);
                if (location.pathname.startsWith(`/${PATH_NAMES.ProcessingWorkflow}`) || currentOrigin === 'scan') {
                    navigate(`/${PATH_NAMES.TechnicianView}`, { replace: true, state: { successSnackbar } });
                } else {
                    setTimeout(() => { showSnackbar(successSnackbar); }, 0);
                }
            }
        } catch (error) {
            console.error('Error beginning processing:', error);
            if (isMobile && !isBulkView) showSnackbar({ message: (error as any)?.response?.data?.detail || 'Error beginning processing', severity: 'error' });
            setShowScanResourceId(false);
            setValidatedEquipmentId('');
        } finally {
            setIsActionInProgress(false);
            if (isMobile && !isBulkView) setLoading(false);
        }
    };

    const handleBeginProcessing = async () => {
        const currentStep = getCurrentStepFromApi();
        const stepToBegin = (currentStep && isCurrentStatus('Not Started')) ? currentStep : undefined;
        if (!stepToBegin || !canBeginStep(stepToBegin)) return;
        if (isRestrictedStepForActionButtons(stepToBegin)) return;
        const equipments = getCurrentStepEquipments(stepToBegin);
        const hasEquipments = equipments.length > 0;
        setValidatedEquipmentId('');
        if (hasEquipments) {
            setScanMode('equipment');
            setShowScanResourceId(true);
            return;
        }
        if (isCurrentStepTreatmentRequired(stepToBegin)) {
            setScanMode('treatment');
            setShowScanResourceId(true);
            return;
        }
        await beginProcessingForStep();
    };

    const handleResourceScanned = async (resourceId: string) => {
        const currentStep = getCurrentStepFromApi();
        const stepToBegin = (currentStep && isCurrentStatus('Not Started')) ? currentStep : undefined;
        if (!stepToBegin) return;
        if (scanMode === 'equipment') {
            const equipmentId = String(resourceId ?? '').trim();
            setValidatedEquipmentId(equipmentId);
            if (isCurrentStepTreatmentRequired(stepToBegin)) {
                setScanMode('treatment');
                return;
            }
            await beginProcessingForStep({ equipment_id: equipmentId });
            return;
        }
        const treatmentName = String(resourceId ?? '').trim();
        await beginProcessingForStep({ equipment_id: validatedEquipmentId || undefined, treatment_name: treatmentName });
    };

    const handleSkipEquipmentScan = async () => {
        const currentStep = getCurrentStepFromApi();
        const stepToBegin = (currentStep && isCurrentStatus('Not Started')) ? currentStep : undefined;
        if (!stepToBegin) return;
        setValidatedEquipmentId('');
        if (isCurrentStepTreatmentRequired(stepToBegin)) {
            setScanMode('treatment');
            return;
        }
        await beginProcessingForStep();
    };

    const handleScanBack = () => {
        if (scanMode === 'treatment' && validatedEquipmentId) {
            setScanMode('equipment');
            return;
        }
        setShowScanResourceId(false);
        setValidatedEquipmentId('');
    };

    const handleMarkAsDone = () => setConfirmDialogOpen(true);

    const handleConfirmMarkAsDone = async () => {
        if (isActionInProgress) return;
        setIsActionInProgress(true);
        if (isMobile && !isBulkView) setLoading(true);
        try {
            const currentStep = getCurrentStepFromApi();
            const inProgressStep = (currentStep && isCurrentStatus('In Progress')) ? currentStep : undefined;
            if (!inProgressStep || !spLotInfo) return;
            if (isRestrictedStepForActionButtons(inProgressStep)) return;
            await markAsDoneMutation.mutateAsync({
                data: {
                    splot_ids: spLotIds,
                    workflow_step_id: inProgressStep.id,
                    workflow_protocol_id: spLotInfo.workflowProtocolId,
                    user_id: globalState?.user_id_db,
                    comment: inProgressStep.comment || ''
                }
            });
            const markDoneSuccessMessage = `${inProgressStep.name} for ${spLotIds[0]} marked as done.`;
            const successSnackbar: HandheldSuccessSnackbar = { message: markDoneSuccessMessage, severity: 'success', duration: 4500 };
            if (isMobile && !isBulkView) {
                setConfirmDialogOpen(false);
                try { await refreshWorkflowDetails(); } catch (refreshError: any) {
                    showSnackbar({ message: refreshError?.response?.data?.detail || 'Error fetching SP Lot data', severity: 'error' });
                }
                setHomePage(true);
                if (location.pathname.startsWith(`/${PATH_NAMES.ProcessingWorkflow}`) || currentOrigin === 'scan') {
                    navigate(`/${PATH_NAMES.TechnicianView}`, { replace: true, state: { successSnackbar } });
                } else {
                    setTimeout(() => { showSnackbar(successSnackbar); }, 0);
                }
            } else {
                setProcessingSteps(prev => prev.map(step => step.id === inProgressStep.id ? { ...step, status: 'Completed' } : step));
                setSpLotInfo(prev => prev ? { ...prev, currentStepStatus: 'Completed' } : prev);
                setConfirmDialogOpen(false);
            }
        } catch (error) {
            console.error('Error marking step as done:', error);
            if (isMobile && !isBulkView) showSnackbar({ message: (error as any)?.response?.data?.detail || 'Error marking step as done', severity: 'error' });
        } finally {
            setIsActionInProgress(false);
            if (isMobile && !isBulkView) setLoading(false);
        }
    };

    const getSkippableStep = () => {
        const currentStep = getCurrentStepFromApi();
        if (!currentStep) return undefined;
        if (!isCurrentStatus('Not Started') && !isCurrentStatus('In Progress')) return undefined;
        return currentStep;
    };

    // Steps the API flags as mandatory cannot be skipped.
    const isSkipStepAllowed = () => {
        const stepToSkip = getSkippableStep();
        if (!stepToSkip || stepToSkip.disabled) return false;
        if (isRestrictedStepForActionButtons(stepToSkip)) return false;
        return stepToSkip.isMandatory !== true;
    };

    const isSkipStepEnabled = () => {
        const stepToSkip = getSkippableStep();
        if (!stepToSkip || !isSkipStepAllowed()) return false;
        return expandedStep === stepToSkip.id;
    };

    const shouldShowSkipButton = () => !isBulkView && isSkipStepEnabled();

    const handleSkipStep = () => {
        if (!isSkipStepAllowed()) return;
        setSkipComment('');
        setSkipDialogOpen(true);
    };

    const handleConfirmSkipStep = async () => {
        if (isActionInProgress) return;
        const stepToSkip = getSkippableStep();
        const comment = skipComment.trim();
        if (!stepToSkip || !spLotInfo || !comment) return;
        setIsActionInProgress(true);
        if (isMobile && !isBulkView) setLoading(true);
        let leavingPage = false;
        try {
            await skipStepMutation.mutateAsync({
                data: {
                    splot_ids: spLotIds,
                    workflow_step_id: stepToSkip.id,
                    workflow_protocol_id: spLotInfo.workflowProtocolId,
                    user_id: globalState?.user_id_db,
                    comment
                }
            });
            setSkipDialogOpen(false);
            setSkipComment('');
            const skipSuccessMessage = `${stepToSkip.name} for ${spLotIds[0]} is skipped.`;
            const successSnackbar: HandheldSuccessSnackbar = { message: skipSuccessMessage, severity: 'success', duration: 4500 };
            if (isMobile && !isBulkView) {
                // Go home straight away - no workflow details refetch, it would flash the steps page.
                leavingPage = true;
                setHomePage(true);
                if (location.pathname.startsWith(`/${PATH_NAMES.ProcessingWorkflow}`) || currentOrigin === 'scan') {
                    navigate(`/${PATH_NAMES.TechnicianView}`, { replace: true, state: { successSnackbar } });
                } else {
                    setTimeout(() => { showSnackbar(successSnackbar); }, 0);
                }
                return;
            }
            try { await refreshWorkflowDetails(); } catch (refreshError: any) {
                showSnackbar({ message: refreshError?.response?.data?.detail || 'Error fetching SP Lot data', severity: 'error' });
            }
        } catch (error) {
            console.error('Error skipping step:', error);
            showSnackbar({ message: (error as any)?.response?.data?.detail || 'Error skipping step', severity: 'error' });
        } finally {
            if (!leavingPage) {
                setIsActionInProgress(false);
                if (isMobile && !isBulkView) setLoading(false);
            }
        }
    };

    const isBeginProcessingEnabled = () => {
        const currentStep = getCurrentStepFromApi();
        if (currentStep && isCurrentStatus('Not Started')) {
            if (isRestrictedStepForActionButtons(currentStep)) return false;
            return expandedStep === currentStep.id && canBeginStep(currentStep);
        }
        return false;
    };

    const hasInProgressStep = () => {
        const currentStep = getCurrentStepFromApi();
        if (currentStep && isCurrentStatus('In Progress')) return true;
        return false;
    };

    const isMarkAsDoneEnabled = () => {
        const currentStep = getCurrentStepFromApi();
        if (currentStep && isCurrentStatus('In Progress')) {
            if (isRestrictedStepForActionButtons(currentStep)) return false;
            return expandedStep === currentStep.id;
        }
        return false;
    };

    const shouldShowButton = () => {
        if (isBulkView) return true;
        const currentStatus = (spLotInfo?.currentStepStatus || '').toLowerCase();
        return currentStatus === 'not started' || currentStatus === 'in progress';
    };

    return {
        spLotIds,
        isBulkView,
        spLotInfo,
        processingSteps,
        expandedStep,
        setExpandedStep,
        commentDialogOpen,
        setCommentDialogOpen,
        materialDialogOpen,
        setMaterialDialogOpen,
        spLotsDialogOpen,
        setSpLotsDialogOpen,
        confirmDialogOpen,
        setConfirmDialogOpen,
        skipDialogOpen,
        setSkipDialogOpen,
        skipComment,
        setSkipComment,
        commentText,
        setCommentText,
        originalCommentText,
        currentStepForComment,
        expandedInfo,
        setExpandedInfo,
        loading,
        showScanResourceId,
        scanMode,
        validatedEquipmentId,
        isActionInProgress,
        classes: useCommonStyles(),
        getCropNameByCode,
        stepRefs,
        handleBackNavigation: () => {
            if (onBack) { onBack(); return; }
            if (currentOrigin === 'scan') { navigate(`/${PATH_NAMES.TechnicianView}`, { state: { openScanner: true } }); return; }
            if (currentOrigin === 'processing-card') { navigate(`/${PATH_NAMES.TechnicianView}`, { state: { openCard: { type: 'in progress', pageTitle: 'Processing' } } }); return; }
            if (currentOrigin === 'idle-card') { navigate(`/${PATH_NAMES.TechnicianView}`, { state: { openCard: { type: 'not started', pageTitle: 'Idle' } } }); return; }
            navigate(`/${PATH_NAMES.TechnicianView}`);
        },
        handleAddComment,
        handleSaveComment,
        handleStepClick: (id: number) => setExpandedStep(id),
        handleBeginProcessing,
        handleResourceScanned,
        handleSkipEquipmentScan,
        handleScanBack,
        handleMarkAsDone,
        handleConfirmMarkAsDone,
        handleSkipStep,
        handleConfirmSkipStep,
        isBeginProcessingEnabled,
        isMarkAsDoneEnabled,
        isSkipStepEnabled,
        shouldShowSkipButton,
        shouldShowButton,
        hasInProgressStep,
        getCurrentStepFromApi,
        isCurrentStatus,
        isCurrentStepEquipmentMandatory: (step?: ProcessingStep) => {
            const equipments = getCurrentStepEquipments(step);
            if (equipments.length === 0) return false;
            return equipments.some((equipment: any) => equipment?.is_mandatory !== false);
        },
    };
};

export default useProcessingWorkflowController;
